/**
 * RelationshipGraphListView — phone-first list of items and 1-hop relationships.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, ChevronDown, ChevronRight, GitBranch } from 'lucide-react';
import { getItemCountState } from '../../collection/types';
import type { ItemRelationshipEdge } from '../../../types/prioritization/itemRelationships';

interface GraphListItem {
  id: string;
  title?: string;
  itemCategory?: string;
}

interface RelationshipGraphListViewProps {
  items: GraphListItem[];
  edges: ItemRelationshipEdge[];
  edgeConfig: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }>;
  uiTypeForStoredType: (type: string) => string;
  search: string;
  onSearchChange: (value: string) => void;
  onSelectItem?: (itemId: string) => void;
}

export function RelationshipGraphListView({
  items,
  edges,
  edgeConfig,
  uiTypeForStoredType,
  search,
  onSearchChange,
  onSelectItem,
}: RelationshipGraphListViewProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const itemMap = useMemo(
    () => new Map(items.map((i) => [i.id, i])),
    [items],
  );

  const edgesByItem = useMemo(() => {
    const map = new Map<string, ItemRelationshipEdge[]>();
    for (const edge of edges) {
      for (const id of [edge.sourceItemId, edge.targetItemId]) {
        if (!map.has(id)) map.set(id, []);
        map.get(id)!.push(edge);
      }
    }
    return map;
  }, [edges]);

  const showSearch = getItemCountState(items.length) === 'many' || search.length > 0;

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-16 text-center px-4">
        <GitBranch className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-3" />
        <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">No items match</p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">Adjust search or filters</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {showSearch && (
        <div className="px-4 pt-3 pb-2 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
            <input
              type="search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search items…"
              aria-label="Search items in relationship list"
              className="w-full pl-9 pr-9 py-2.5 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
            />
            {search && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-ter)]"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] px-1">
          List view — open on a wider screen for the full graph canvas.
        </p>

        {items.map((item) => {
          const itemEdges = edgesByItem.get(item.id) ?? [];
          const expanded = expandedId === item.id;

          return (
            <div
              key={item.id}
              className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : item.id)}
                className="w-full flex items-start gap-2 px-3 py-3 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug truncate">
                    {item.title || 'Untitled'}
                  </p>
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                    {item.itemCategory || 'Item'}
                    {itemEdges.length > 0 && (
                      <span> · {itemEdges.length} relationship{itemEdges.length !== 1 ? 's' : ''}</span>
                    )}
                  </p>
                </div>
              </button>

              {expanded && (
                <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 space-y-2 bg-[var(--surface)]/50 dark:bg-[color-mix(in_srgb,var(--bg-raised)_40%,transparent)]">
                  {itemEdges.length === 0 ? (
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] italic py-1">No visible relationships</p>
                  ) : (
                    itemEdges.map((edge) => {
                      const isSource = edge.sourceItemId === item.id;
                      const partnerId = isSource ? edge.targetItemId : edge.sourceItemId;
                      const partner = itemMap.get(partnerId);
                      const uiType = uiTypeForStoredType(edge.type);
                      const cfg = edgeConfig[uiType] ?? edgeConfig.related;
                      const Icon = cfg?.icon ?? GitBranch;

                      return (
                        <div
                          key={edge.id}
                          className="flex items-start gap-2 text-xs px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)]"
                        >
                          <span
                            className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full mt-0.5"
                            style={{ backgroundColor: `${cfg.color}18`, color: cfg.color }}
                          >
                            <Icon className="h-3 w-3" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                              {isSource ? '→' : '←'} {cfg.label}
                            </p>
                            <Link
                              to={`/prioritization/items/${partnerId}`}
                              className="text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline truncate block"
                              onClick={() => onSelectItem?.(partnerId)}
                            >
                              {partner?.title || `${partnerId.slice(0, 8)}…`}
                            </Link>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <Link
                    to={`/prioritization/items/${item.id}`}
                    className="inline-flex text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline pt-1"
                  >
                    Open item detail
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
